import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReservationSummaryModal from './ReservationSummaryModal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";

const ReservationContainer = styled.div`
  padding: 20px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin-top: 20px;
  width: 100%;
  border: 2px solid #007bff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Price = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const InputLabel = styled.label`
  font-size: 0.9rem;
  margin-bottom: 5px;
  font-weight: bold;
`;

const Slider = styled.input`
  width: 100%;
  margin-bottom: 15px;
`;

const Select = styled.select`
  width: calc(50% - 10px);
  padding: 8px;
  margin-bottom: 15px;
  margin-right: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-sizing: border-box;
`;

const DateTimeContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DateTimeSelector = styled.div`
  width: 48%;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  margin-top: 15px;
  margin-bottom: 15px;
`;

const CheckboxContainer = styled.div`
  display: column;
  justify-content: space-between;
  margin: 10px 20px 10px 20px
`;

const ReserveButton = styled.button`
  padding: 12px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 20px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const TotalCostContainer = styled.div`
  text-align: right;
  margin-top: 1px;
  font-size: 1rem;
`;

const Reservation = ({ id, price, maxOccupants, isAuthenticated }) => {
  const [occupants, setOccupants] = useState(1);
  const [includeOvernight, setIncludeOvernight] = useState(false);
  const [showCheckInOut, setShowCheckInOut] = useState(false);
  const [includeSaturdays, setIncludeSaturdays] = useState(false);
  const [includeSundays, setIncludeSundays] = useState(false);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();


  useEffect(() => {
    const today = new Date();
    setCheckInDate(new Date(today.setDate(today.getDate() + 1)));
    setCheckOutDate(new Date(today.setDate(today.getDate() + 2)));
  }, []);

  const handleOvernightChange = () => {
    setIncludeOvernight((prev) => !prev);
    setShowCheckInOut(!showCheckInOut);
  };

  const calculateTotalHours = () => {
    let totalHours = 0;
    if (!checkInDate || !checkOutDate || !checkInTime || !checkOutTime) {
      return 0;
    }

    if (includeOvernight){
      // Combine check-in date and time
      const checkInDateTime = new Date(checkInDate);
      checkInDateTime.setHours(checkInTime.getHours());
      checkInDateTime.setMinutes(checkInTime.getMinutes());
  
      // Combine check-out date and time
      const checkOutDateTime = new Date(checkOutDate);
      checkOutDateTime.setHours(checkOutTime.getHours());
      checkOutDateTime.setMinutes(checkOutTime.getMinutes());
  
      // Calculate the difference in milliseconds and convert to hours
      const timeDifference = checkOutDateTime - checkInDateTime;
      totalHours = timeDifference / 36e5; // Convert milliseconds to hours
    } else {
      // Calculate the difference in hours between check-in and check-out time
    const checkInTimeInMinutes = checkInTime.getHours() * 60 + checkInTime.getMinutes();
    const checkOutTimeInMinutes = checkOutTime.getHours() * 60 + checkOutTime.getMinutes();
    const timeDifferenceInMinutes = checkOutTimeInMinutes - checkInTimeInMinutes;

    // Calculate the number of days between check-in and check-out dates
    const checkInDateCopy = new Date(checkInDate);
    const checkOutDateCopy = new Date(checkOutDate);
    let timeDifferenceInDays = (checkOutDateCopy - checkInDateCopy) / (1000 * 3600 * 24); // Convert milliseconds to days

    while (checkInDateCopy <= checkOutDateCopy) {
      const day = checkInDateCopy.getDay(); // Get day of the week (0 = Sunday, 6 = Saturday)
      if (day === 0 && !includeSundays) timeDifferenceInDays -= 1; // Sunday charge
      if (day === 6 && !includeSaturdays) timeDifferenceInDays -= 1; // Saturday charge
      checkInDateCopy.setDate(checkInDateCopy.getDate() + 1); // Move to the next day
    }

    // Multiply the time difference (in hours) by the number of days
    totalHours = (timeDifferenceInMinutes / 60) * (timeDifferenceInDays + 1); // +1 to include the check-in day
  }
    const totalHoursToDisplay = totalHours.toFixed(2)
    return totalHoursToDisplay;
  };

  const handleReserveClick = () => {
    console.log(isAuthenticated)
    if (!isAuthenticated) {
      const currentUrl = window.location.pathname;
      navigate(`/login?redirectUrl=${currentUrl}`);
      return;
    } else {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
      <ReservationContainer>
        <Price>${price} per hour</Price>

      <InputLabel>{t('occupants')}</InputLabel>
      <Slider
        type="range"
        min="1"
        max={maxOccupants}
        value={occupants}
        onChange={(e) => setOccupants(e.target.value)}
      />
      <span>{occupants} {occupants > 1 ? t('occupants') : t('occupant')}</span>

      <CheckboxLabel>
        <input
          type="checkbox"
          checked={includeOvernight}
          onChange={handleOvernightChange}
        />
        &nbsp; {t('includeOvernight')}?
      </CheckboxLabel>

      {includeOvernight ? (
        <>
          <div>
            <DateTimeContainer>
              <DateTimeSelector>
                <InputLabel>{t('checkinDate')}</InputLabel>
                <DatePicker
                  selected={checkInDate}
                  onChange={(date) => setCheckInDate(date)}
                  dateFormat="yyyy/MM/dd"
                  minDate={new Date()}
                  placeholderText={t('selectcheckinDate')}
                />
              </DateTimeSelector>
              <DateTimeSelector>
                <InputLabel>{t('checkinTime')}</InputLabel>
                <DatePicker
                  selected={checkInTime}
                  onChange={(date) => setCheckInTime(date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="HH:mm"
                  placeholderText={t('selectCheckinTime')}
                />
              </DateTimeSelector>
            </DateTimeContainer>
            <DateTimeContainer>
              <DateTimeSelector>
                <InputLabel>{t('checkoutDate')}</InputLabel>
                <DatePicker
                  selected={checkOutDate}
                  onChange={(date) => setCheckOutDate(date)}
                  dateFormat="yyyy/MM/dd"
                  minDate={new Date()}
                  placeholderText={t('selectCheckoutDate')}
                />
              </DateTimeSelector>
              <DateTimeSelector>
                <InputLabel>{t('checkoutTime')}</InputLabel>
                <DatePicker
                  selected={checkOutTime}
                  onChange={(date) => setCheckOutTime(date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="HH:mm"
                  placeholderText={t('selectCheckoutTime')}
                />
              </DateTimeSelector>
            </DateTimeContainer>
          </div>
        </>
      ) : (
        <>
          <div>
            <DateTimeContainer>
              <DateTimeSelector>
                <InputLabel>{t('startDate')}</InputLabel>
                <DatePicker
                  selected={checkInDate}
                  onChange={(date) => setCheckInDate(date)}
                  dateFormat="yyyy/MM/dd"
                  minDate={new Date()}
                  placeholderText={t('selectDates')}
                />
              </DateTimeSelector>
              <DateTimeSelector>
                <InputLabel>{t('endDate')}</InputLabel>
                <DatePicker
                  selected={checkOutDate}
                  onChange={(date) => setCheckOutDate(date)}
                  dateFormat="yyyy/MM/dd"
                  minDate={new Date()}
                  placeholderText={t('selectDates')}
                />
              </DateTimeSelector>
            </DateTimeContainer>
            <DateTimeContainer>
              <DateTimeSelector>
                <InputLabel>{t('startTime')}</InputLabel>
                <DatePicker
                  selected={checkInTime}
                  onChange={(date) => setCheckInTime(date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="HH:mm"
                  placeholderText={t('selectDailyTime')}
                />
              </DateTimeSelector>
              <DateTimeSelector>
                <InputLabel>{t('endDate')}</InputLabel>
                <DatePicker
                  selected={checkOutTime}
                  onChange={(date) => setCheckOutTime(date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="HH:mm"
                  placeholderText={t('selectDailyTime')}
                />
              </DateTimeSelector>
            </DateTimeContainer>
            <CheckboxContainer>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={includeSaturdays}
                  onChange={() => setIncludeSaturdays(!includeSaturdays)}
                />
                &nbsp; {t('includeSaturdays')}
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={includeSundays}
                  onChange={() => setIncludeSundays(!includeSundays)}
                />
                &nbsp; {t('includeSundays')}
              </CheckboxLabel>
              </CheckboxContainer>
          </div>
        </>
      )}
      <ReserveButton onClick={handleReserveClick}>{t('reserve')}</ReserveButton>
      <TotalCostContainer>
        <p>{t('totalHours')}: {calculateTotalHours()} {t('hours')}</p>
        <p>{t('cost')}: ${calculateTotalHours() * price} ({t('excluding')} VAT)</p>
        <p>VAT: ${calculateTotalHours() * price * 0.255} (@ 25.5% VAT)</p>
        <p>{t('totalCost')}: ${(calculateTotalHours() * price) + (calculateTotalHours() * price * 0.255)}</p>

      </TotalCostContainer>
      
      {isModalOpen && (
        <ReservationSummaryModal
          closeModal={closeModal}
          id={id}
          occupants={occupants}
          includeOvernight={includeOvernight}
          includeSaturdays={includeSaturdays}
          includeSundays={includeSundays}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          checkInTime={checkInTime}
          checkOutTime={checkOutTime}
          totalHours={calculateTotalHours()}
          totalCost={Number(calculateTotalHours() * price).toFixed(2)}
        />
      )}
        
      
  </ReservationContainer>
  );
};

export default Reservation;
